/**
 * authLock — bound the wait on the auth token's cross-tab lock.
 *
 * WHY THIS EXISTS
 *
 * supabase-js serializes token refresh across tabs with the Web Locks
 * API: every `getSession()` and every refresh takes an exclusive lock
 * named `lock:sb-<ref>-auth-token`. With no `auth.lock` option the SDK
 * uses its own `navigatorLock`, and for the long-lived calls it passes
 * `acquireTimeout = -1`, which means no `AbortSignal` is attached and
 * `navigator.locks.request` waits FOREVER.
 *
 * So one tab that holds the lock and never gives it back — crashed,
 * suspended by the OS, or stuck mid-refresh behind a dead network —
 * freezes `getSession()` in every other tab, permanently. The whole
 * app shell is gated behind that call (`useAuth`), so what the studio
 * sees is "We couldn't reach your account", with a backend that is
 * perfectly healthy and not one request in the network panel. Reload
 * does not fix it, because the new tab queues behind the same lock.
 * Quitting the browser does, which is a terrible thing to have to know.
 *
 * WHAT THIS DOES INSTEAD
 *
 * Waits a bounded time for the lock, and if it cannot be had, RUNS
 * ANYWAY. The lock exists to stop two tabs refreshing the same token
 * at once; losing that race costs at worst one redundant refresh, and
 * the SDK already tolerates it. An app that will not load costs
 * everything. Those are not close.
 *
 * Deliberately not `processLock`: that is in-memory per tab and gives
 * up cross-tab coordination entirely. This keeps the lock working for
 * the 99.9% case and only degrades when it is already broken.
 *
 * This lives in its own file because `client.ts` is generated — "This
 * file is automatically generated. Do not edit it directly." — so the
 * only thing that has to survive a regeneration there is one import
 * and one option. If auth ever hangs like this again, check that
 * `lock: boundedAuthLock` is still in `client.ts`.
 */

/** How long to wait for the lock before deciding it is not coming. */
const ACQUIRE_TIMEOUT_MS = 5_000;

type LockManagerLike = {
  request: (
    name: string,
    options: { mode: 'exclusive'; signal?: AbortSignal },
    fn: () => Promise<unknown>,
  ) => Promise<unknown>;
};

/**
 * Matches supabase-js's `LockFunc`. `acquireTimeout` is what the SDK
 * asks for: -1 means "wait forever", which is the thing being fixed,
 * so it is treated as "wait the bounded time". A positive value the
 * SDK sets for itself is honoured when it is shorter.
 */
export async function boundedAuthLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  const locks = (globalThis.navigator as Navigator & { locks?: LockManagerLike })?.locks;

  // No Web Locks at all — older Safari, some embedded webviews, or a
  // context where the API is blocked. The SDK's own lock degrades the
  // same way rather than throwing.
  if (!locks) return await fn();

  const limit = acquireTimeout > 0 ? Math.min(acquireTimeout, ACQUIRE_TIMEOUT_MS) : ACQUIRE_TIMEOUT_MS;

  const controller = new AbortController();
  // `granted` is what keeps a genuine failure inside `fn` from being
  // mistaken for a timeout. `signal.aborted` can be true even after the
  // lock was granted (aborting a granted request is a no-op per spec),
  // so without this flag a thrown error from `fn` would be swallowed
  // and `fn` would run a second time.
  let granted = false;
  const timer = setTimeout(() => controller.abort(), limit);

  try {
    return (await locks.request(
      name,
      { mode: 'exclusive', signal: controller.signal },
      async () => {
        granted = true;
        clearTimeout(timer);
        return await fn();
      },
    )) as R;
  } catch (err) {
    if (!granted && controller.signal.aborted) {
      // Could not get the lock in time. Another tab is holding it and
      // is not coming back. Proceed unserialized rather than hanging
      // the app shell forever.
      return await fn();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
